import { tWithKeyAttr as t } from "../../../i18nHelper";
import styles from "./appointmentsProgramSchedulePopup.module.css";
import {
  useDeleteAppointmentMutation,
  useDeleteAppointmentsProgramWithAllAppointmentsMutation,
  useGetAppointmentsProgramsByUserIdMutation,
  useGetAppointmentsProgramsScheduleMutation,
  useLazyGetGroupTreatmentPlansQuery,
} from "../../../services/api/utils.api";
import {
  IAppointment,
  TAppointmentsProgramsData,
} from "../../../services/types/utils.types";
import React, { useEffect, useState } from "react";
import { useAppSelector } from "../../../services/hooks";
import { appointmentsProgramsScheduleSelector } from "../../../services/selectors/utils.selectors";
import Modal from "../modal/Modal";
import DeleteConfirmation from "../deleteConfirmation/deleteConfirmation";
import ErrorPopup from "../errorPopup/errorPopup";
import { getPrintDate, isDateAfterToday } from "../../../utils/utils";
import Loader from "../../loader/loader";
import GeneralSuccessPopup from "../generalSuccessPopup/generalSuccessPopup";
import { adminSelector } from "../../../services/selectors/user.selectors";
import { week } from "../../../utils/constants";
import ListPagination from "../../listPagination/listPagination";
import { useFetcher } from "react-router-dom";

function AppointmentsProgramSchedulePopup({
  program,
}: {
  program: TAppointmentsProgramsData;
}) {
  const admin = useAppSelector(adminSelector);

  const appointmentsProgramsSchedule = useAppSelector(
    appointmentsProgramsScheduleSelector
  );

  const [itemToDelete, setItemToDelete] = useState<IAppointment | undefined>(
    undefined
  );
  const [openModalError, setOpenModalError] = useState(false);
  const [openModalSuccess, setOpenModalSuccess] = useState(false);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [limits, setLimits] = useState({ start: 1, end: 8 });
  const step = 8;
  const [getSchedule, { status }] =
    useGetAppointmentsProgramsScheduleMutation();
  const [deleteAppointment] = useDeleteAppointmentMutation();
  const [deleteProgramWithAppointments] =
    useDeleteAppointmentsProgramWithAllAppointmentsMutation();
  const [getAppointmentsPrograms] =
    useGetAppointmentsProgramsByUserIdMutation();
  const [GetGroupTreatmentPlans] = useLazyGetGroupTreatmentPlansQuery();

  useEffect(() => {
    getSchedule({ id: program.appointmentsProgram.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const getAppointmentStatus = (a: IAppointment) => {
    return a.active === false ? t('DONE') : a.statusId === 1 ? t('STATUS_ACTIVE') : t('DELETED');
  };
  const activeAppointmentsCount = appointmentsProgramsSchedule.filter(
    (a) => a.statusId === 1 && a.active !== false
  ).length;
  const deletePopupText =
    activeAppointmentsCount === 1
      ? t('CONFIRM_DELETE_SINGLE_MEETING')
      : t('REMOVE_SINGLE_APPOINTMENT_CONFIRM', {
        count: activeAppointmentsCount,
        id: itemToDelete?.id,
        cancelAll: t('CANCEL_ALL_MEETINGS')
      })
  // console.log(activeAppointmentsCount, deletePopupText);

  return (
    <article className={styles.popup}>
      {status !== "fulfilled" ? (
        <Loader />
      ) : appointmentsProgramsSchedule.length > 0 ? (
        <>
          <h3 className={styles.sectionTitle}>{t('PROGRAM_MEETING_DATES')}</h3>
          <ul className={`list ${styles.scrollableList}`}>
            <li className={`listRow listHeader ${styles.listRow_header} `}>
              <p className={`listCell`}>{t('NUMBER_1')}</p>
              <p className={`listCell`}>{t('MEETING_DATE')}</p>
              <p className={`listCell`}>{t('DAY_OF_WEEK')}</p>
              <p className={`listCell`}>{t('START_TIME')}</p>
              <p className={`listCell`}>{t('END_TIME')}</p>
              <p className={`listCell`}>{t('COLUMN_STATUS')}</p>
              <p className={`listCell`}>{t('COLUMN_OPTIONS')}</p>
            </li>
            {appointmentsProgramsSchedule
              .slice(limits.start - 1, limits.end)
              .map((a, index) => (
                <li
                  key={a.id}
                  className={`listRow ${index % 2 === 0 ? "listRow_colored" : ""
                    } ${styles.listRow}`}
                >
                  <p className={`listCell`}>{a.id}</p>
                  <p className={`listCell`}>
                    {getPrintDate(new Date(a?.appointmentDate))}
                  </p>
                  <p className={`listCell`}>
                    {week[new Date(a.appointmentDate)?.getDay()] || ""}
                  </p>
                  <p className={`listCell`}>
                    {a.startTime?.split(":")?.slice(0, 2)?.join(":") || "-"}
                  </p>
                  <p className={`listCell`}>
                    {a.endTime?.split(":")?.slice(0, 2)?.join(":") || "-"}
                  </p>
                  <p className={`listCell`}>{getAppointmentStatus(a)}</p>
                  {isDateAfterToday(new Date(a.appointmentDate)) &&
                    a?.statusId === 1 ? (
                    <p className={`listCell`}>
                      <button
                        className={`btn btn_type_delete ${styles.delete}`}
                        onClick={() => setItemToDelete(a)}
                      ></button> </p>
                  ) : (
                    <p className={`listCell centered-text`}>{t('LOCKED')}</p>
                  )}
                </li>
              ))}
          </ul>
          <ListPagination
            limits={limits}
            total={appointmentsProgramsSchedule.length}
            setLimits={setLimits}
            step={step}
          />
          {activeAppointmentsCount > 0 ? (
            <button
              type={"button"}
              className={`button button_white ${styles.btn}`}
              onClick={() => {
                setOpenDeleteConfirmation(true);
                setItemToDelete(undefined);
              }}
            >{t('CANCEL_ALL_MEETINGS')}</button>
          ) : (
            <p className={"text"}>{t('NO_ACTIVE_MEETINGS')}</p>
          )}
        </>
      ) : (
        <p className={styles.text}>{t('NO_MEETINGS_FOUND_1')}</p>
      )}
      {itemToDelete?.appointmentDate && (
        <Modal onClose={() => setItemToDelete(undefined)}>
          <DeleteConfirmation
            text={deletePopupText}
            btnText={t('CANCEL_MEETING')}
            onDelete={async () => {
              try {
                const { appointmentsProgram } = program;
                const {
                  usersId,
                  therapistsId,
                  ...filteredAppointmentsProgram
                } = appointmentsProgram;
                if (activeAppointmentsCount > 1) {
                  await deleteAppointment({
                    ...filteredAppointmentsProgram,
                    deleteType: 1,
                    dateToDelete: itemToDelete?.appointmentDate,
                    updateUsersId: admin.id,
                  });
                } else {
                  const res = await deleteProgramWithAppointments({
                    id: program.appointmentsProgram.id,
                    updateUsersId: admin.id,
                  });

                  if (res && "data" in res) {
                    setOpenModalSuccess(true);
                  }
                }

                if (program.user?.guid) {
                  await getAppointmentsPrograms({
                    companyId: admin.companyId,
                    usersGuid: program.user.guid,
                  });
                } else if (appointmentsProgram.treatmentGroupId) {
                  await GetGroupTreatmentPlans({
                    groupId: appointmentsProgram.treatmentGroupId,
                  });
                }

              } catch (err) {
                setOpenModalError(true);
                console.log(err);
              } finally {
                setItemToDelete(undefined);
                setOpenDeleteConfirmation(false);
              }
            }}
            onClose={() => {
              setItemToDelete(undefined);
              setOpenDeleteConfirmation(false);
            }}
          />
        </Modal>
      )}
      {openDeleteConfirmation && program && (
        <Modal
          onClose={() => {
            setOpenDeleteConfirmation(false);
          }}
        >
          <DeleteConfirmation
            text={t('CONF_CANCEL_ALL_PROGRAM_MEETINGS', { programName: program?.appointmentsProgram?.name })}
            btnText={t('CANCEL_ALL_MEETINGS')}
            onDelete={() => {
              deleteProgramWithAppointments({
                id: program.appointmentsProgram.id,
                updateUsersId: admin.id,
                // updateDate: formatDateTime(new Date()),
              })
                .then((res) => {
                  if (program.user?.guid) {
                    getAppointmentsPrograms({
                      companyId: admin.companyId,
                      usersGuid: program.user.guid,
                    });}
                    else if(program.appointmentsProgram.treatmentGroupId){
                      GetGroupTreatmentPlans({
                        groupId: program.appointmentsProgram.treatmentGroupId!,
                      });
                    }
                    if (res && "data" in res) {
                      setOpenModalSuccess(true);
                    }
                  })
                .catch((err) => {
                  setOpenModalError(true);
                  console.log(err);
                });
            }}
            onClose={() => {
              setOpenDeleteConfirmation(false);
            }}
          />
        </Modal>
      )}
      {openModalSuccess && (
        <Modal onClose={() => setOpenModalSuccess(false)}>
          <GeneralSuccessPopup
            onClose={() => {
              setOpenModalSuccess(false);
            }}
          />
        </Modal>
      )}
      {openModalError && (
        <Modal onClose={() => setOpenModalError(false)}>
          <ErrorPopup onClose={() => setOpenModalError(false)} />
        </Modal>
      )}
    </article>
  );
}
export default AppointmentsProgramSchedulePopup;
